/**
 * BudgetSettingsModal Component
 *
 * Modal for configuring budget threshold and alert settings.
 * Allows users to set spending limits for batch enrichment operations.
 *
 * Props:
 * - show (boolean) - Control modal visibility
 * - onHide (function) - Close modal callback
 * - currentBudget (object) - Current budget settings {threshold, alertEnabled}
 * - onSave (function) - Async function to save new settings, returns {success, error}
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Form, Button, Alert } from 'react-bootstrap';

const BudgetSettingsModal = ({ show, onHide, currentBudget, onSave }) => {
  const [threshold, setThreshold] = useState(10.00);
  const [alertEnabled, setAlertEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  // Initialize form with current budget when modal opens
  useEffect(() => {
    if (show && currentBudget) {
      setThreshold(currentBudget.threshold || 10.00);
      setAlertEnabled(currentBudget.alertEnabled !== undefined ? currentBudget.alertEnabled : true);
    }
  }, [show, currentBudget]);

  const handleSave = async () => {
    setSaving(true);

    try {
      const result = await onSave({
        threshold: parseFloat(threshold),
        alertEnabled: alertEnabled
      });

      if (result?.success) {
        // Close modal on successful save
        onHide();
      } else {
        // Log error but don't show UI error in v1
        console.error('[BudgetSettingsModal] Save failed:', result?.error);
      }
    } catch (error) {
      console.error('[BudgetSettingsModal] Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset to current values
    if (currentBudget) {
      setThreshold(currentBudget.threshold || 10.00);
      setAlertEnabled(currentBudget.alertEnabled !== undefined ? currentBudget.alertEnabled : true);
    }
    onHide();
  };

  return (
    <Modal show={show} onHide={handleCancel} centered>
      <Modal.Header closeButton>
        <Modal.Title>Budget Settings</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Alert variant="info" className="mb-3">
          <Alert.Heading as="h6" className="mb-2">
            Cost Control
          </Alert.Heading>
          <p className="mb-0" style={{ fontSize: '0.9rem' }}>
            Set a budget threshold to control AI enrichment costs. You'll see running costs
            and warnings when approaching or exceeding your limit.
          </p>
        </Alert>

        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Budget Threshold (USD)</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              disabled={saving}
            />
            <Form.Text className="text-muted">
              You'll be warned before starting batches that exceed this amount.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              id="alert-enabled-checkbox"
              label="Enable budget alerts"
              checked={alertEnabled}
              onChange={(e) => setAlertEnabled(e.target.checked)}
              disabled={saving}
            />
            <Form.Text className="text-muted">
              Show warnings when running cost exceeds your threshold.
            </Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={handleCancel}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

BudgetSettingsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  currentBudget: PropTypes.shape({
    threshold: PropTypes.number,
    alertEnabled: PropTypes.bool,
    updatedAt: PropTypes.string
  }),
  onSave: PropTypes.func.isRequired
};

BudgetSettingsModal.defaultProps = {
  currentBudget: {
    threshold: 10.00,
    alertEnabled: true
  }
};

export default BudgetSettingsModal;
